import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

export function AppAnalyticsTracker() {
  const location = useLocation();
  const gaId = process.env.REACT_APP_GA_ID;
  const isDev = process.env.NODE_ENV === 'development';

  // Stable values to satisfy ESLint
  const stableGaConfig = useMemo(
    () => ({ gaId, isDev }),
    [gaId, isDev]
  );

  // --------------------------------------------
  // INITIALIZATION (runs once at mount)
  // --------------------------------------------
  useEffect(() => {
    if (!stableGaConfig.gaId) {
      console.warn('⚠️ REACT_APP_GA_ID not configured');
      return;
    }

    if (stableGaConfig.isDev) {
      console.log(`📊 [DEV] GA init: ${stableGaConfig.gaId}`);
      return;
    }

    // Load GA script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${stableGaConfig.gaId}`;
    document.head.appendChild(script);

    // Init gtag
    window.dataLayer = window.dataLayer || [];
    // eslint-disable-next-line prefer-rest-params
    window.gtag = window.gtag || function() {
      // @ts-ignore - gtag.js requires the Arguments object, not an array
      window.dataLayer?.push(arguments);
    };

    window.gtag('js', new Date());
    window.gtag('config', stableGaConfig.gaId, {
      anonymize_ip: true,
      allow_google_signals: false,
    });

    console.log(`✅ GA initialized in PROD: ${stableGaConfig.gaId}`);
  }, [stableGaConfig]); // ESLint happy

  // --------------------------------------------
  // PAGEVIEW TRACKING (runs on route change)
  // --------------------------------------------
  useEffect(() => {
    if (!stableGaConfig.gaId) return;

    const path = location.pathname;
    const title = document.title;

    if (stableGaConfig.isDev) {
      console.log(`📍 [DEV] Pageview: ${path} (${title})`);
    } else if (window.gtag) {
      window.gtag('config', stableGaConfig.gaId, {
        page_path: path,
        page_title: title,
      });
      console.log(`📍 Pageview tracked: ${path}`);
    }
  }, [location.pathname, stableGaConfig]);

  return null;
}

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}
